import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaCube,
  FaDraftingCompass,
  FaIndustry,
  FaPrint,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import Seo, { getSiteUrl } from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";
import { getLocalizedPath } from "../i18n/localizedRoutes";
import getApiBase from "../utils/apiBase";
import { useAuth } from "./AuthContext";
import { createLocalizedError, localizedErrorMessage } from "./customDesignUtils";
import { trackAnalyticsEvent } from "../utils/analytics";
import "./CustomDesign.css";

const API_BASE = getApiBase();
const acceptedExtensions = new Set(["png", "jpg", "jpeg", "webp", "pdf", "step", "stp", "dxf"]);
const maxFileSize = 25 * 1024 * 1024;

const services = [
  {
    id: "prototype",
    icon: FaCube,
    titleKey: "design.service.prototype.title",
    descriptionKey: "design.service.prototype.description",
  },
  {
    id: "small_batch",
    icon: FaPrint,
    titleKey: "design.service.smallBatch.title",
    descriptionKey: "design.service.smallBatch.description",
  },
  {
    id: "mold",
    icon: FaIndustry,
    titleKey: "design.service.mold.title",
    descriptionKey: "design.service.mold.description",
  },
];

const processSteps = [
  ["01", "design.process.step1.title", "design.process.step1.description"],
  ["02", "design.process.step2.title", "design.process.step2.description"],
  ["03", "design.process.step3.title", "design.process.step3.description"],
  ["04", "design.process.step4.title", "design.process.step4.description"],
  ["05", "design.process.step5.title", "design.process.step5.description"],
];

const countryOptions = [
  "US", "CA", "CN", "JP", "KR", "HK", "SG", "AU", "AE", "AT", "BE", "BR",
  "CH", "CL", "CZ", "DE", "DK", "ES", "FI", "FR", "GB", "IE", "IL", "IN",
  "IT", "MX", "NL", "NO", "NZ", "PL", "PT", "SE", "TH", "ZA",
];

const initialForm = {
  title: "",
  serviceType: "prototype",
  description: "",
  dimensions: "",
  quantity: "",
  material: "",
  preferredProcess: "not_sure",
  targetTimeline: "flexible",
  countryCode: "",
};

const readError = async (response, fallback) => {
  await response.json().catch(() => ({}));
  return fallback;
};

const CustomDesign = () => {
  const navigate = useNavigate();
  const { t, formatNumber, formatRegion, locale } = useI18n();
  const fileInputRef = useRef(null);
  const { user, loading: authLoading, login, register } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [drawings, setDrawings] = useState([]);
  const [fileError, setFileError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [draftProjectId, setDraftProjectId] = useState("");
  const [uploadedFileKeys, setUploadedFileKeys] = useState([]);
  const [authMode, setAuthMode] = useState("");
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [authSubmitting, setAuthSubmitting] = useState(false);

  const totalSize = useMemo(
    () => drawings.reduce((total, file) => total + file.size, 0),
    [drawings],
  );

  const updateForm = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const addDrawings = (fileList) => {
    setFileError("");
    const next = Array.from(fileList || []);
    if (!next.length) return;
    if (drawings.length + next.length > 10) {
      setFileError(t("design.error.maxFiles", { count: 10 }));
      return;
    }
    for (const file of next) {
      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      if (!acceptedExtensions.has(extension)) {
        setFileError(t("design.error.unsupportedFile", { fileName: file.name }));
        return;
      }
      const limit = ["step", "stp"].includes(extension) ? maxFileSize : 10 * 1024 * 1024;
      if (file.size > limit) {
        setFileError(t("design.error.fileTooLarge", { fileName: file.name }));
        return;
      }
    }
    const combinedSize = totalSize + next.reduce((total, file) => total + file.size, 0);
    if (combinedSize > 150 * 1024 * 1024) {
      setFileError(t("design.error.totalSize"));
      return;
    }
    setDrawings((current) => [...current, ...next]);
  };

  const removeDrawing = (index) => {
    setDrawings((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const validate = () => {
    if (!form.title.trim()) return t("design.error.titleRequired");
    if (form.description.trim().length < 10) return t("design.error.descriptionRequired");
    if (!form.countryCode) return t("design.error.countryRequired");
    if (!drawings.length) return t("design.error.fileRequired");
    return "";
  };

  const submitProject = async () => {
    const validationError = validate();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      let projectId = draftProjectId;
      if (!projectId) {
        const createResponse = await fetch(`${API_BASE}/api/custom-design/projects`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (!createResponse.ok) {
          throw createLocalizedError(await readError(createResponse, t("design.error.create")));
        }
        const created = await createResponse.json();
        projectId = created.project.id;
        setDraftProjectId(projectId);
      }
      const alreadyUploaded = new Set(uploadedFileKeys);
      for (const file of drawings) {
        const fileKey = `${file.name}:${file.size}:${file.lastModified}`;
        if (alreadyUploaded.has(fileKey)) continue;
        const filePayload = new FormData();
        filePayload.append("files", file);
        filePayload.append("kind", "drawing");
        const fileResponse = await fetch(`${API_BASE}/api/custom-design/projects/${projectId}/files`, {
          method: "POST",
          credentials: "include",
          body: filePayload,
        });
        if (!fileResponse.ok) {
          throw createLocalizedError(await readError(fileResponse, t("design.error.upload")));
        }
        alreadyUploaded.add(fileKey);
        setUploadedFileKeys(Array.from(alreadyUploaded));
      }
      const submitResponse = await fetch(`${API_BASE}/api/custom-design/projects/${projectId}/submit`, {
        method: "POST",
        credentials: "include",
      });
      if (!submitResponse.ok) {
        throw createLocalizedError(await readError(submitResponse, t("design.error.submit")));
      }
      trackAnalyticsEvent('custom_request_submit', { target: 'custom_design.submit' });
      setDraftProjectId("");
      setUploadedFileKeys([]);
      navigate(`/account/custom-projects/${projectId}?submitted=true`);
    } catch (error) {
      setSubmitError(localizedErrorMessage(error, t("design.error.submit")));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    if (authLoading) return;
    if (!user) {
      setAuthMode("login");
      return;
    }
    submitProject();
  };

  const handleAuth = async (event) => {
    event.preventDefault();
    setAuthSubmitting(true);
    setAuthError("");
    try {
      if (authMode === "register") {
        await register(authForm);
      } else {
        await login({ email: authForm.email, password: authForm.password });
      }
      setAuthMode("");
      await submitProject();
    } catch (error) {
      setAuthError(t("design.error.auth"));
    } finally {
      setAuthSubmitting(false);
    }
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    url: `${getSiteUrl()}${getLocalizedPath('/custom-design', locale)}`,
    name: t("design.schema.name"),
    provider: { "@type": "Organization", name: "YCH-YOYO" },
    description: t("design.schema.description"),
    areaServed: t("design.schema.area"),
  };

  return (
    <main className="custom-design-page">
      <Seo
        title={t("design.seo.title")}
        description={t("design.seo.description")}
        path="/custom-design"
        image="/og.png"
        structuredData={pageSchema}
      />

      <section className="custom-design-hero">
        <div className="custom-design-hero__copy">
          <p className="custom-design-kicker">{t("design.hero.kicker")}</p>
          <h1>{t("design.hero.title")}</h1>
          <p className="custom-design-hero__intro">{t("design.hero.intro")}</p>
          <div className="custom-design-hero__actions">
            <a data-analytics-target="custom_design.start" className="custom-design-button custom-design-button--primary" href="#start-project">
              {t("design.hero.start")} <FaArrowRight aria-hidden="true" />
            </a>
            {user ? (
              <Link className="custom-design-button custom-design-button--text" to="/account/custom-projects">
                {t("design.hero.projects")}
              </Link>
            ) : (
              <a className="custom-design-button custom-design-button--text" href="#how-it-works">{t("design.hero.how")}</a>
            )}
          </div>
          <ul className="custom-design-proof" aria-label={t("design.hero.highlights")}>
            <li><FaCheck aria-hidden="true" /> {t("design.hero.no3d")}</li>
            <li><FaCheck aria-hidden="true" /> {t("design.hero.worldwide")}</li>
            <li><FaCheck aria-hidden="true" /> {t("design.hero.scale")}</li>
          </ul>
        </div>

        <div className="custom-design-blueprint" aria-label={t("design.blueprint.aria")}>
          <div className="blueprint-grid" />
          <div className="blueprint-label blueprint-label--top">{t("design.blueprint.concept")}</div>
          <div className="blueprint-shape blueprint-shape--back" />
          <div className="blueprint-shape blueprint-shape--front"><span /></div>
          <div className="blueprint-measure blueprint-measure--width"><span>Ø 68 mm</span></div>
          <div className="blueprint-measure blueprint-measure--height"><span>42 mm</span></div>
          <div className="blueprint-note">{t("design.blueprint.sketch")}</div>
          <div className="blueprint-arrow">→</div>
          <div className="blueprint-cube"><span className="blueprint-cube__top" /><span className="blueprint-cube__side" /></div>
          <div className="blueprint-note blueprint-note--model">{t("design.blueprint.model")}</div>
          <div className="blueprint-status"><span /> {t("design.blueprint.ready")}</div>
        </div>
      </section>

      <section className="custom-design-trust" aria-label={t("design.trust.aria")}>
        <span>{t("design.trust.industrial")}</span><span>{t("design.trust.modeling")}</span><span>{t("design.trust.printing")}</span>
        <span>{t("design.trust.cnc")}</span><span>{t("design.trust.mold")}</span>
      </section>

      <section className="custom-design-process" id="how-it-works">
        <div className="custom-design-section-heading">
          <p className="custom-design-kicker">{t("design.process.kicker")}</p>
          <h2>{t("design.process.title")}</h2>
          <p>{t("design.process.description")}</p>
        </div>
        <div className="custom-design-process__grid">
          {processSteps.map(([number, titleKey, descriptionKey]) => (
            <article className="custom-design-process__step" key={number}>
              <span className="custom-design-process__number">{number}</span>
              <h3>{t(titleKey)}</h3>
              <p>{t(descriptionKey)}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="custom-design-project" id="start-project">
        <div className="custom-design-project__intro">
          <p className="custom-design-kicker">{t("design.start.kicker")}</p>
          <h2>{t("design.start.title")}</h2>
          <p>{t("design.start.description")}</p>
          <div className="custom-design-drawing-tips">
            <FaDraftingCompass aria-hidden="true" />
            <div>
              <strong>{t("design.start.tipsTitle")}</strong>
              <span>{t("design.start.tips")}</span>
            </div>
          </div>
        </div>

        <form className="custom-design-form" onSubmit={handleSubmit}>
          <fieldset>
            <legend>{t("design.service.legend")}</legend>
            <div className="custom-design-service-options">
              {services.map(({ id, icon: Icon, titleKey, descriptionKey }) => (
                <label className={`custom-design-service-card${form.serviceType === id ? " is-selected" : ""}`} key={id}>
                  <input type="radio" name="serviceType" value={id} checked={form.serviceType === id} onChange={() => updateForm("serviceType", id)} />
                  <Icon aria-hidden="true" /><strong>{t(titleKey)}</strong><span>{t(descriptionKey)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend>{t("design.form.basics")}</legend>
            <div className="custom-design-fields">
              <label className="custom-design-fields__wide">
                {t("design.form.title")}
                <input type="text" value={form.title} maxLength="120" placeholder={t("design.form.titlePlaceholder")} onChange={(event) => updateForm("title", event.target.value)} required />
              </label>
              <label>
                {t("design.form.dimensions")}
                <input type="text" value={form.dimensions} maxLength="500" placeholder={t("design.form.dimensionsPlaceholder")} onChange={(event) => updateForm("dimensions", event.target.value)} />
              </label>
              <label>
                {t("design.form.quantity")}
                <select value={form.quantity} onChange={(event) => updateForm("quantity", event.target.value)}>
                  <option value="">{t("design.form.quantityPlaceholder")}</option><option value="1">{t("design.form.quantityOne")}</option>
                  <option value="2-10">{t("design.form.quantityTwoTen")}</option><option value="11-100">{t("design.form.quantityElevenHundred")}</option>
                  <option value="101-1000">{t("design.form.quantityHundredThousand")}</option><option value="1000+">{t("design.form.quantityMore")}</option>
                </select>
              </label>
              <label>
                {t("design.form.material")}
                <input type="text" value={form.material} maxLength="300" placeholder={t("design.form.materialPlaceholder")} onChange={(event) => updateForm("material", event.target.value)} />
              </label>
              <label>
                {t("design.form.preferredProcess")}
                <select value={form.preferredProcess} onChange={(event) => updateForm("preferredProcess", event.target.value)}>
                  <option value="not_sure">{t("design.form.process.notSure")}</option><option value="3d_printing">{t("design.form.process.printing")}</option>
                  <option value="cnc">{t("design.form.process.cnc")}</option><option value="molding">{t("design.form.process.molding")}</option>
                </select>
              </label>
              <label>
                {t("design.form.targetTimeline")}
                <select value={form.targetTimeline} onChange={(event) => updateForm("targetTimeline", event.target.value)}>
                  <option value="flexible">{t("design.form.timeline.flexible")}</option><option value="2-4_weeks">{t("design.form.timeline.twoFourWeeks")}</option>
                  <option value="1-2_months">{t("design.form.timeline.oneTwoMonths")}</option><option value="3+_months">{t("design.form.timeline.threePlusMonths")}</option>
                </select>
              </label>
              <label>
                {t("design.form.country")}
                <select value={form.countryCode} onChange={(event) => updateForm("countryCode", event.target.value)} required>
                  <option value="">{t("design.form.countryPlaceholder")}</option>
                  {countryOptions.map((code) => <option key={code} value={code}>{formatRegion(code)}</option>)}
                </select>
              </label>
              <label className="custom-design-fields__wide">
                {t("design.form.description")}
                <textarea value={form.description} maxLength="4000" rows="5" placeholder={t("design.form.descriptionPlaceholder")} onChange={(event) => updateForm("description", event.target.value)} required />
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend>{t("design.upload.legend")}</legend>
            <button data-analytics-target="custom_design.upload" className={`custom-design-upload${drawings.length ? " has-file" : ""}`} type="button" onClick={() => fileInputRef.current?.click()}>
              <FaUpload aria-hidden="true" />
              <span>
                <strong>{drawings.length ? t("design.upload.selected", { count: drawings.length }) : t("design.upload.choose")}</strong>
                <small>{t("design.upload.help")}</small>
              </span>
              <b>{t("design.upload.browse")}</b>
            </button>
            <input ref={fileInputRef} className="custom-design-file-input" aria-label={t("design.upload.legend")} type="file" multiple accept=".png,.jpg,.jpeg,.webp,.pdf,.step,.stp,.dxf" onChange={(event) => { addDrawings(event.target.files); event.target.value = ""; }} />
            {drawings.length ? (
              <ul className="custom-design-file-list">
                {drawings.map((file, index) => (
                  <li key={`${file.name}-${file.lastModified}-${index}`}>
                    <span><strong>{file.name}</strong><small>{formatNumber(file.size / 1024 / 1024, { maximumFractionDigits: 2 })} MB</small></span>
                    <button type="button" aria-label={t("design.upload.remove", { fileName: file.name })} onClick={() => removeDrawing(index)}><FaTimes /></button>
                  </li>
                ))}
              </ul>
            ) : null}
            {fileError && <p className="custom-design-form-error" role="alert">{fileError}</p>}
          </fieldset>

          {submitError && <p className="custom-design-submit-error" role="alert">{submitError}</p>}
          <button data-analytics-target="custom_design.submit" className="custom-design-button custom-design-button--submit" type="submit" disabled={submitting || authLoading}>
            {submitting ? t("design.submit.submitting") : user ? t("design.submit.review") : t("design.submit.login")}
            {!submitting && <FaArrowRight aria-hidden="true" />}
          </button>
          <p className="custom-design-form-note">{t("design.submit.note")}</p>
        </form>
      </section>

      <section className="custom-design-deliverables">
        <div className="custom-design-deliverables__card">
          <p className="custom-design-kicker">{t("design.next.kicker")}</p>
          <h2>{t("design.next.title")}</h2>
          <div className="custom-design-deliverables__list">
            <div><span>01</span><p><strong>{t("design.next.feasibilityTitle")}</strong>{t("design.next.feasibilityDescription")}</p></div>
            <div><span>02</span><p><strong>{t("design.next.designTitle")}</strong>{t("design.next.designDescription")}</p></div>
            <div><span>03</span><p><strong>{t("design.next.approvalTitle")}</strong>{t("design.next.approvalDescription")}</p></div>
            <div><span>04</span><p><strong>{t("design.next.prototypeTitle")}</strong>{t("design.next.prototypeDescription")}</p></div>
          </div>
        </div>
        <aside className="custom-design-quote-card">
          <span className="custom-design-quote-card__badge">{t("design.quote.response")}</span>
          <strong>{t("design.quote.responseTime")}</strong>
          <p>{t("design.quote.complex")}</p>
          <hr />
          <span className="custom-design-quote-card__label">{t("design.quote.payOnly")}</span>
          <ul><li><FaCheck /> {t("design.quote.design")}</li><li><FaCheck /> {t("design.quote.prototype")}</li><li><FaCheck /> {t("design.quote.shipping")}</li></ul>
        </aside>
      </section>

      <section className="custom-design-contact">
        <p className="custom-design-kicker">{t("design.contact.kicker")}</p>
        <h2>{t("design.contact.title")}</h2>
        <Link className="custom-design-button custom-design-button--light" to="/account/custom-projects">
          {t("design.contact.open")} <FaArrowRight aria-hidden="true" />
        </Link>
      </section>

      {authMode ? (
        <div className="custom-design-auth-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !authSubmitting) setAuthMode(""); }}>
          <form className="custom-design-auth-dialog" onSubmit={handleAuth} role="dialog" aria-modal="true" aria-labelledby="custom-design-auth-title">
            <button className="custom-design-auth-close" type="button" aria-label={t("modal.close")} onClick={() => setAuthMode("")}><FaTimes /></button>
            <p className="custom-design-kicker">{t("design.auth.kicker")}</p>
            <h2 id="custom-design-auth-title">{authMode === "register" ? t("design.auth.registerTitle") : t("design.auth.loginTitle")}</h2>
            <p>{t("design.auth.description")}</p>
            {authMode === "register" ? (
              <label>{t("common.name")}<input type="text" value={authForm.name} maxLength="120" onChange={(event) => setAuthForm((current) => ({ ...current, name: event.target.value }))} /></label>
            ) : null}
            <label>{t("common.email")}<input type="email" value={authForm.email} onChange={(event) => setAuthForm((current) => ({ ...current, email: event.target.value }))} required /></label>
            <label>{t("common.password")}<input type="password" minLength="8" value={authForm.password} onChange={(event) => setAuthForm((current) => ({ ...current, password: event.target.value }))} required /></label>
            {authError && <p className="custom-design-form-error" role="alert">{authError}</p>}
            <button className="custom-design-button custom-design-button--submit" type="submit" disabled={authSubmitting}>
              {authSubmitting ? t("design.auth.wait") : authMode === "register" ? t("design.auth.createSubmit") : t("design.auth.loginSubmit")}
            </button>
            <button className="custom-design-auth-switch" type="button" onClick={() => { setAuthError(""); setAuthMode(authMode === "register" ? "login" : "register"); }}>
              {authMode === "register" ? t("design.auth.switchLogin") : t("design.auth.switchRegister")}
            </button>
          </form>
        </div>
      ) : null}
    </main>
  );
};

export default CustomDesign;
